import React, { useState, useEffect } from 'react';
import worksheetService from '../../services/worksheetService';

const WorksheetPrintModal = ({
  isOpen,
  onClose,
  initialTestIds = [],
  availableTemplates = []
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [layoutType, setLayoutType] = useState('PatientWise');
  const [paperSize, setPaperSize] = useState('A4');
  const [orientation, setOrientation] = useState('Portrait');
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [includeBarcode, setIncludeBarcode] = useState(true);
  const [includeSignature, setIncludeSignature] = useState(true);

  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(85);

  // Auto-select default template if available
  useEffect(() => {
    if (isOpen && availableTemplates.length > 0) {
      const defaultTpl = availableTemplates.find((t) => t.isDefault) || availableTemplates[0];
      if (defaultTpl) {
        setSelectedTemplateId(defaultTpl.templateId);
        setLayoutType(defaultTpl.layoutType || 'PatientWise');
        setPaperSize(defaultTpl.paperSize || 'A4');
        setOrientation(defaultTpl.orientation || 'Portrait');
      }
    }
  }, [isOpen, availableTemplates]);

  // Fetch live print preview whenever configuration changes
  useEffect(() => {
    if (!isOpen || initialTestIds.length === 0) return;

    let isMounted = true;
    const loadPreview = async () => {
      setIsLoadingPreview(true);
      try {
        const payload = {
          templateId: selectedTemplateId ? parseInt(selectedTemplateId, 10) : null,
          testIds: initialTestIds,
          paperSize,
          orientation,
          includeHeader,
          includeFooter,
          includeBarcode,
          includeSignature,
          layoutTypeOverride: layoutType
        };

        const res = await worksheetService.getPrintPreview(payload);
        if (isMounted && res.success && res.data) {
          setPreviewData(res.data);
        }
      } catch (err) {
        console.error('Failed to load print preview:', err);
      } finally {
        if (isMounted) setIsLoadingPreview(false);
      }
    };

    loadPreview();
    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    selectedTemplateId,
    layoutType,
    paperSize,
    orientation,
    includeHeader,
    includeFooter,
    includeBarcode,
    includeSignature,
    initialTestIds
  ]);

  if (!isOpen) return null;

  const handleTemplateSelect = (tplId) => {
    setSelectedTemplateId(tplId);
    const tpl = availableTemplates.find((t) => t.templateId === parseInt(tplId, 10));
    if (tpl) {
      setLayoutType(tpl.layoutType || 'PatientWise');
      setPaperSize(tpl.paperSize || 'A4');
      setOrientation(tpl.orientation || 'Portrait');
    }
  };

  const handleBrowserPrint = () => {
    try {
      const sheetEl = document.getElementById('ws-printable-sheet-content');
      if (!sheetEl) {
        window.print();
        return;
      }

      const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Laboratory Worksheet - ${previewData?.template?.templateName || previewData?.centreName || 'LIMS'}</title>
  <style>
    @page {
      size: ${paperSize} ${orientation.toLowerCase()};
      margin: 15mm 12mm 15mm 12mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #0f172a;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
    }
    .ws-print-root {
      width: 100%;
      margin: 0 auto;
      padding-top: 8px; /* Prevents top header and date from being cut off */
    }
    table {
      width: 100%;
      border-collapse: collapse;
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    th, td {
      border: 1px solid #cbd5e1;
      padding: 5px 8px;
    }
    th {
      background: #f1f5f9 !important;
      font-weight: 700;
      font-size: 11px;
    }
    .barcode-pill {
      font-family: monospace;
      font-size: 11px;
      font-weight: 700;
      background: #e0f2fe;
      padding: 2px 6px;
      border-radius: 3px;
      border: 1px solid #bae6fd;
    }
    .stat-tag {
      background: #fee2e2;
      color: #dc2626;
      border: 1px solid #fca5a5;
      font-size: 9px;
      font-weight: 800;
      padding: 1px 5px;
      border-radius: 3px;
    }
    .routine-tag {
      background: #f1f5f9;
      color: #475569;
      font-size: 9px;
      font-weight: 600;
      padding: 1px 5px;
      border-radius: 3px;
    }
  </style>
</head>
<body>
  <div class="ws-print-root">
    ${sheetEl.innerHTML}
  </div>
</body>
</html>`;

      let iframe = document.getElementById('ws-hidden-print-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'ws-hidden-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
      }

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
      iframe.contentWindow.focus();
      setTimeout(() => {
        iframe.contentWindow.print();
      }, 350);
    } catch (err) {
      console.error('Print failed:', err);
      window.print();
    }
  };

  const handleExportPdf = () => {
    handleBrowserPrint();
  };

  return (
    <div className="ws-modal-overlay" onClick={onClose}>
      <div
        className="ws-modal-card"
        style={{ width: '1240px', maxWidth: '96vw', height: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="ws-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'rgba(56, 189, 248, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                border: '1px solid rgba(56, 189, 248, 0.3)'
              }}
            >
              🖨️
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', letterSpacing: '0.2px' }}>
                Laboratory Worksheet Studio &amp; Print Engine
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>
                Configured Layout &bull; {previewData?.totalRecords || initialTestIds.length} Specimen(s) Ready
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Zoom Controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                background: 'rgba(255, 255, 255, 0.08)',
                padding: '3px 6px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px'
              }}
            >
              <button
                type="button"
                className="ws-btn ws-btn-sm"
                style={{
                  background: 'transparent',
                  color: '#ffffff',
                  border: 'none',
                  padding: '2px 8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                onClick={() => setZoomLevel((z) => Math.max(z - 15, 50))}
                title="Zoom Out"
              >
                −
              </button>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  minWidth: '40px',
                  textAlign: 'center',
                  color: '#38bdf8'
                }}
              >
                {zoomLevel}%
              </span>
              <button
                type="button"
                className="ws-btn ws-btn-sm"
                style={{
                  background: 'transparent',
                  color: '#ffffff',
                  border: 'none',
                  padding: '2px 8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                onClick={() => setZoomLevel((z) => Math.min(z + 15, 160))}
                title="Zoom In"
              >
                +
              </button>
              <button
                type="button"
                className="ws-btn ws-btn-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  color: '#e2e8f0',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
                onClick={() => setZoomLevel(85)}
                title="Fit Page (85%)"
              >
                Fit Page
              </button>
            </div>

            <button
              type="button"
              className="ws-btn ws-btn-primary"
              style={{
                background: '#0284c7',
                color: '#ffffff',
                border: 'none',
                fontWeight: 600,
                padding: '7px 16px',
                borderRadius: '7px',
                boxShadow: '0 2px 4px rgba(2, 132, 199, 0.3)'
              }}
              onClick={handleBrowserPrint}
            >
              🖨️ Print Worksheet
            </button>
            <button
              type="button"
              className="ws-btn"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#f8fafc',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                fontWeight: 600,
                padding: '7px 14px',
                borderRadius: '7px'
              }}
              onClick={handleExportPdf}
            >
              📄 Export PDF
            </button>
            <button
              type="button"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#94a3b8',
                border: 'none',
                borderRadius: '7px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '15px'
              }}
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Configuration Bar */}
        <div className="ws-print-toolbar">
          <div className="ws-print-toolbar-group">
            {/* Template Master Select */}
            <div className="ws-print-field">
              <span className="ws-print-field-label">Template Preset</span>
              <select
                className="ws-print-select"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
              >
                <option value="">-- Custom / Direct Layout --</option>
                {availableTemplates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.templateName} ({t.layoutType}) {t.isDefault ? '⭐ [Default]' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* 14 Layout Formats */}
            <div className="ws-print-field">
              <span className="ws-print-field-label">Layout Format</span>
              <select
                className="ws-print-select"
                value={layoutType}
                onChange={(e) => setLayoutType(e.target.value)}
              >
                <option value="PatientWise">1. Patient-Wise (Patient → Tests → Params)</option>
                <option value="TestWise">2. Test-Wise (Matrix: Test → Patients Columns)</option>
                <option value="PatientPlusTest">3. Patient + Test Grid</option>
                <option value="PatientPlusParameters">4. Patient + Parameters Breakdown</option>
                <option value="TestPlusPatients">5. Test + Patients Breakdown</option>
                <option value="SampleWise">6. Sample-Wise Accession</option>
                <option value="AnalyzerWise">7. Analyzer / Instrument Bench Run</option>
                <option value="BatchWise">8. Batch-Wise Run Manifest</option>
                <option value="DepartmentWise">9. Department-Wise</option>
                <option value="SectionWise">10. Section-Wise (Hematology, Bio, etc.)</option>
                <option value="TechnicianWise">11. Technician-Wise Workbench</option>
                <option value="Compact">12. Compact High-Density (Small Lab / STAT)</option>
                <option value="Detailed">13. Detailed Full Reference &amp; Methods</option>
                <option value="Custom">14. Custom Formatted</option>
              </select>
            </div>

            {/* Paper Size */}
            <div className="ws-print-field">
              <span className="ws-print-field-label">Paper Size</span>
              <select
                className="ws-print-select ws-print-select-sm"
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value)}
              >
                <option value="A4">A4 (210 × 297 mm)</option>
                <option value="A5">A5 (148 × 210 mm)</option>
                <option value="Letter">US Letter</option>
              </select>
            </div>

            {/* Orientation */}
            <div className="ws-print-field">
              <span className="ws-print-field-label">Orientation</span>
              <select
                className="ws-print-select ws-print-select-sm"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
              >
                <option value="Portrait">Portrait</option>
                <option value="Landscape">Landscape</option>
              </select>
            </div>
          </div>

          {/* Toggle Pills */}
          <div className="ws-print-toggles-group">
            <span className="ws-print-field-label">Sections to Include</span>
            <div className="ws-print-pills">
              <label className={`ws-toggle-pill ${includeHeader ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={includeHeader}
                  onChange={(e) => setIncludeHeader(e.target.checked)}
                />
                Header
              </label>
              <label className={`ws-toggle-pill ${includeFooter ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={includeFooter}
                  onChange={(e) => setIncludeFooter(e.target.checked)}
                />
                Footer
              </label>
              <label className={`ws-toggle-pill ${includeBarcode ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={includeBarcode}
                  onChange={(e) => setIncludeBarcode(e.target.checked)}
                />
                Barcode
              </label>
              <label className={`ws-toggle-pill ${includeSignature ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={includeSignature}
                  onChange={(e) => setIncludeSignature(e.target.checked)}
                />
                Signatures
              </label>
            </div>
          </div>
        </div>

        {/* Live Sheet Viewport */}
        <div className="ws-modal-body ws-preview-sheet-frame">
          {isLoadingPreview ? (
            <div style={{ padding: '80px 20px', textAlign: 'center', color: '#ffffff' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔄</div>
              <div>Rendering live laboratory worksheet preview...</div>
            </div>
          ) : !previewData ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#ffffff' }}>
              No print preview available.
            </div>
          ) : (
            <div
              style={{
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.15s ease'
              }}
            >
              {/* Render Simulated Printable Sheet */}
              <div
                id="ws-printable-sheet-content"
                className={`ws-paper-sheet ${
                  orientation === 'Landscape' ? 'ws-paper-a4-landscape' : 'ws-paper-a4-portrait'
                }`}
              >
                {/* Printable Header */}
                {includeHeader && (
                  <div
                    style={{
                      borderBottom: '2px solid #000000',
                      paddingBottom: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, textTransform: 'uppercase' }}>
                        {previewData.centreName || 'CENTRAL PATHOLOGY DIAGNOSTIC LABORATORY'}
                      </h2>
                      <div style={{ fontSize: '13px', fontWeight: 700, marginTop: '2px', color: '#1e293b' }}>
                        WORKSHEET / TECHNICIAN WORKBENCH — {previewData.template?.templateName || previewData.templateName || 'Standard Worksheet'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#475569' }}>
                        Layout: <strong>{layoutType || previewData.template?.layoutType || 'PatientWise'}</strong> • Generated:{' '}
                        {new Date().toLocaleString()}
                      </div>
                    </div>
                    {includeBarcode && (
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 800,
                            letterSpacing: '3px',
                            fontSize: '15px',
                            border: '1px solid #000',
                            padding: '4px 8px'
                          }}
                        >
                          *WS-{Date.now().toString().slice(-6)}*
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>CONFIDENTIAL LAB RECORD</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Printable Body Content depending on Layout */}
                {renderPreviewLayout(previewData, layoutType)}

                {/* Printable Footer */}
                {includeFooter && (
                  <div
                    style={{
                      borderTop: '1px solid #000000',
                      paddingTop: '14px',
                      marginTop: '28px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      fontSize: '11px'
                    }}
                  >
                    <div>
                      <div>
                        Operator / Technician: <strong>{previewData.generatedBy || 'Technician'}</strong>
                      </div>
                      <div>Total Specimens: {previewData.totalSamples || previewData.totalRecords || previewData.samples?.length || 0}</div>
                    </div>
                    {includeSignature && (
                      <div style={{ display: 'flex', gap: '36px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ width: '130px', borderBottom: '1px dotted #000', height: '24px' }} />
                          <div style={{ marginTop: '4px', fontWeight: 600 }}>Technician Sign</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ width: '130px', borderBottom: '1px dotted #000', height: '24px' }} />
                          <div style={{ marginTop: '4px', fontWeight: 600 }}>Pathologist / Supervisor</div>
                        </div>
                      </div>
                    )}
                    <div style={{ textAlign: 'right', color: '#64748b' }}>
                      Page 1 of 1
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Pure layout generator for simulated live print preview
 */
function renderPreviewLayout(previewData, layoutType) {
  let groups = previewData.groups || [];

  // Transform backend samples list into printable layout groups if groups are not pre-split
  if (groups.length === 0 && previewData.samples && previewData.samples.length > 0) {
    if (layoutType === 'TestWise' || layoutType === 'TestPlusPatients') {
      const byTest = {};
      previewData.samples.forEach((s) => {
        const key = s.testName || 'Laboratory Test';
        if (!byTest[key]) byTest[key] = [];
        if (s.observations && s.observations.length > 0) {
          s.observations.forEach((obs) => {
            byTest[key].push({
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: obs.parameterName,
              currentValue: obs.currentValue,
              readingFormat: obs.readingFormat,
              minRange: obs.minRange,
              maxRange: obs.maxRange,
              displayReading: obs.displayReading,
              methodName: s.analyzerName || 'Benchtop'
            });
          });
        } else {
          byTest[key].push({
            sampleNo: s.sampleNo,
            labNo: s.labNo,
            patientName: s.patientName,
            age: s.ageYear,
            gender: s.gender,
            priority: s.priority,
            testName: s.testName,
            parameterName: s.testName,
            currentValue: '',
            readingFormat: '',
            displayReading: 'Normal',
            methodName: s.analyzerName || 'Benchtop'
          });
        }
      });
      groups = Object.keys(byTest).map((tName) => ({
        groupTitle: tName,
        rows: byTest[tName]
      }));
    } else {
      groups = previewData.samples.map((s) => {
        const seen = new Set();
        const uniqueObservations = (s.observations || []).filter((obs) => {
          const key = obs.observationId || obs.parameterName;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        const rows = uniqueObservations.length > 0
          ? uniqueObservations.map((obs) => ({
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              sampleType: s.sampleType,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: obs.parameterName,
              currentValue: obs.currentValue,
              readingFormat: obs.unit || obs.readingFormat || '-',
              minRange: obs.minRange,
              maxRange: obs.maxRange,
              displayReading: obs.referenceRange || obs.displayReading || 'Normal',
              methodName: obs.machineMethod || obs.methodName || s.analyzerName || 'Manual / Bench'
            }))
          : [{
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              sampleType: s.sampleType,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: s.testName,
              currentValue: '',
              readingFormat: '-',
              displayReading: 'Normal',
              methodName: s.analyzerName || 'Manual / Bench'
            }];

        return {
          groupTitle: `${s.patientName} (${s.ageGender || `${s.ageYear}/${s.gender || 'M'}`})`,
          rows
        };
      });
    }
  }

  if (groups.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>No specimen rows in preview.</div>;
  }

  // 1. Test-Wise Matrix format (High-volume laboratory layout)
  if (layoutType === 'TestWise' || layoutType === 'TestPlusPatients') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {groups.map((group, gIdx) => (
          <div key={gIdx} style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ background: '#f1f5f9', padding: '6px 10px', fontWeight: 800, fontSize: '13px', borderBottom: '1px solid #000' }}>
              TEST: {group.groupTitle} ({group.rows?.length || 0} Patients)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #000' }}>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '30px' }}>#</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '90px' }}>Sample No</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '90px' }}>Lab No</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>Patient Demographics</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '50px' }}>Pri</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '140px' }}>Parameter(s)</th>
                  <th style={{ padding: '5px', textAlign: 'left', width: '120px' }}>Result / Bench Entry</th>
                </tr>
              </thead>
              <tbody>
                {group.rows?.map((row, rIdx) => (
                  <tr key={rIdx} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{rIdx + 1}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px', fontWeight: 700 }}>{row.sampleNo}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{row.labNo}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>
                      <strong>{row.patientName}</strong> ({row.age}/{row.gender})
                    </td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px', fontWeight: row.priority === 'STAT' ? 800 : 400 }}>
                      {row.priority}
                    </td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{row.parameterName || row.testName}</td>
                    <td style={{ padding: '5px', borderBottom: '1px dotted #999', background: '#fafafa' }}>
                      {row.currentValue || '________________'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }

  // 2. Compact Layout format (High density)
  if (layoutType === 'Compact') {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead>
          <tr style={{ background: '#e2e8f0', borderBottom: '2px solid #000' }}>
            <th style={{ padding: '4px', textAlign: 'left' }}>Sample No</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Lab No</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Patient</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Test / Parameters</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Result</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Unit &amp; Normal Range</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Pri</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g) => g.rows || []).map((row, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #ccc' }}>
              <td style={{ padding: '4px', fontWeight: 700 }}>{row.sampleNo}</td>
              <td style={{ padding: '4px' }}>{row.labNo}</td>
              <td style={{ padding: '4px' }}>
                {row.patientName} ({row.age}/{row.gender})
              </td>
              <td style={{ padding: '4px', fontWeight: 600 }}>{row.testName} - {row.parameterName || ''}</td>
              <td style={{ padding: '4px', borderBottom: '1px dotted #888' }}>
                {row.currentValue || '_________'}
              </td>
              <td style={{ padding: '4px', fontSize: '10px' }}>
                {row.readingFormat} ({row.displayReading || 'Normal'})
              </td>
              <td style={{ padding: '4px', fontWeight: row.priority === 'STAT' ? 800 : 400 }}>
                {row.priority}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // 3. Default Patient-Wise / Patient+Parameters / Detailed layout
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {groups.map((group, gIdx) => (
        <div key={gIdx} style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
          {/* Patient Header Box */}
          <div
            style={{
              background: '#f8fafc',
              padding: '8px 12px',
              borderBottom: '1px solid #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div>
              <span style={{ fontSize: '13px', fontWeight: 800 }}>{group.groupTitle}</span>
              <span style={{ fontSize: '11px', color: '#475569', marginLeft: '10px' }}>
                Lab No: <strong>{group.rows?.[0]?.labNo}</strong> • Sample:{' '}
                <strong>{group.rows?.[0]?.sampleNo}</strong> ({group.rows?.[0]?.sampleType || 'Blood'})
              </span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700 }}>
              Priority: {group.rows?.[0]?.priority || 'Routine'}
            </div>
          </div>

          {/* Observations Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '22%' }}>Test / Parameter</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '18%' }}>Manual Result Entry</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '12%' }}>Unit</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '24%' }}>Biological Reference Interval</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '24%' }}>Method / Analyzer</th>
              </tr>
            </thead>
            <tbody>
              {group.rows?.map((row, rIdx) => (
                <tr key={rIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                    {row.parameterName || row.testName}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px dotted #999', background: '#fafafa' }}>
                    {row.currentValue || ''}
                  </td>
                  <td style={{ padding: '6px 10px', color: '#475569' }}>{row.readingFormat || '-'}</td>
                  <td style={{ padding: '6px 10px', fontSize: '11px', color: '#475569' }}>
                    {row.minRange || row.maxRange ? `${row.minRange || 0} - ${row.maxRange || ''}` : row.displayReading || 'Normal'}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: '11px', color: '#64748b' }}>
                    {row.methodName || 'Benchtop'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default WorksheetPrintModal;
